import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { JobPosition } from "../types";
import ContentTag from "../../../components/content-tag/ContentTag";

const CardContainer = styled(motion.div)`
  width: 100%;
  padding: 32px 0px 0px;
  min-height: 40dvh;
  overflow-y: hidden;

  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background-color: #f0f0f0;

  /* Card drop shadow */
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
  
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 4px 8px 16px 0px rgba(0, 0, 0, 0.1);
  }

  @media (width > 960px) {
    width: calc(100% / 2 - 16px / 2);
  }
`;

const CardTitle = styled.h3.attrs({
  className: "heading-spacing-1824",
})`
  padding: 0 16px 8px;
  color: #333;
`;

const CardSummary = styled.p.attrs({
  className: "bodytext u-line-height-150 u-text-justify",
})`
  padding: 0 16px 16px;
  color: #666;
`;

const CardImage = styled(motion.img)`
  width: 100%;
  border-radius: 0 0 12px 12px;
  display: block;
`;

const ArrowIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  position: absolute;
  bottom: 16px;
  right: 16px;
  font-size: 24px;
  color: var(--soremo-blue);
  opacity: 0.6;
  transition: all 0.3s ease;
  
  ${CardContainer}:hover & {
    opacity: 1;
    transform: translateX(4px);
  }
`;

const NewLabel = styled.div.attrs({
  className: "bodytext-11 u-line-height-100",
})`
  position: absolute;
  min-width: 20px;
  height: 42px;
  top: 0px;
  right: 12px;
  z-index: 2;
  
  display: flex;
  align-items: center;
  justify-content: center;
  
  background-color: #009ace;
  color: #fff;
  padding: 8px 8px;
  border-radius: 0px 0px 6px 6px;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 16px;
`;

interface CareersJobCardProps {
  job: JobPosition;
  onClick: () => void;
}

const CareersJobCard: React.FC<CareersJobCardProps> = ({ job, onClick }) => {
  return (
    <CardContainer
      className="u-relative"
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      {job.isNew && <NewLabel>NEW</NewLabel>}
      <CardTitle>{job.title}</CardTitle>
      {job.tags && job.tags.length > 0 && (
        <TagContainer>
          {job.tags.map((tag, index) => (
            <ContentTag key={index}>{tag}</ContentTag>
          ))}
        </TagContainer>
      )}
      <CardSummary>{job.summary}</CardSummary>
      {job.image && (
        <CardImage
          src={job.image}
          alt={job.title}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        />
      )}
      <ArrowIcon>chevron_right</ArrowIcon>
    </CardContainer>
  );
};

export default CareersJobCard;
